import { useRootStore } from "store";
import {
  ArticleCategory,
  ArticleCategory2s,
  ArticleCategory3s
} from "models/inventory/inventory";
import { Icon } from "components/icon/Icon";
import Dropdown from "components/dropdown/Dropdown";
import { captureEvent } from "components/posthog/customEventCapture";

import "./InventoryRow.scss";

type Props = {
  category1s?: ArticleCategory[];
  category2s?: ArticleCategory2s[];
  category3s?: ArticleCategory3s[];
  id: number;
  category1Id: number;
  category2Id: number;
  category3Id: number;
  comment: string;
  itemId: number;
  itemComment: string;
  unitId: number;
  isDisabled: boolean;
  isDeleteDisabled: boolean;
  isInternalUser: boolean;
  quantity: number;
  onCategoryChange: (id: number, value: number, category: 1 | 2 | 3) => void;
  onCommentChange: (id: number, value: string) => void;
  onItemChange: (id: number, value: string) => void;
  onQuantityChange: (id: number, value: string) => void;
  onUnitChange: (id: number, value: string) => void;
  onRemoveRow: (value: number) => void;
};

export default function InventoryRow({
  category1s,
  category2s,
  category3s,
  id,
  category1Id,
  category2Id,
  category3Id,
  comment,
  itemId,
  itemComment,
  unitId,
  isDisabled,
  isDeleteDisabled,
  isInternalUser,
  quantity,
  onCategoryChange,
  onCommentChange,
  onItemChange,
  onQuantityChange,
  onUnitChange,
  onRemoveRow
}: Props) {
  const { articleRegister } = useRootStore(
    (state) => state.inventory
  );

  if (!articleRegister) {
    return <></>;
  }

  const { items, units } = articleRegister;

  const category1options: { value: string; label: string }[] =
    category1s?.map((category) => ({
      value: category.id.toString(),
      label: category.name
    })) || [];

  const category2options: { value: string; label: string }[] =
    category2s?.map((category) => ({
      value: category.id.toString(),
      label: category.name
    })) || [];

  const category3options: { value: string; label: string }[] =
    category3s?.map((category) => ({
      value: category.id.toString(),
      label: category.name
    })) || [];

  const itemOptions = items
    .filter((item) => item.category3_id === category3Id)
    .map((item) => ({
      value: item.id.toString(),
      label: item.name
    }));

  const unitOptions = units.map((unit) => ({
    value: unit.id.toString(),
    label: unit.name
  }));

  return (
    <div key={id}>
      <div className="inventory-row">
        <div className="inventory-row-grid">
          <Dropdown
            isClearable={false}
            isSearchable={false}
            options={category1options}
            className="selectmd"
            value={category1Id.toString()}
            placeholder={category1options && category1options[0].label}
            isDisabled={isDisabled}
            onChange={(selected) => {
              if (selected && !Array.isArray(selected)) {
                const selectedOption = selected as {
                  value: string;
                  label: string;
                };
                onCategoryChange(id, parseInt(selectedOption.value), 1);
              }
            }}
          />

          <Dropdown
            isClearable={false}
            isSearchable={false}
            options={category2options}
            className="selectmd"
            value={category2Id.toString()}
            placeholder={category2options[0]?.label || ""}
            isDisabled={isDisabled}
            onChange={(selected) => {
              if (selected && !Array.isArray(selected)) {
                const selectedOption = selected as {
                  value: string;
                  label: string;
                };
                onCategoryChange(id, parseInt(selectedOption.value), 2);
              }
            }}
          />

          <Dropdown
            isClearable={false}
            isSearchable={false}
            options={category3options}
            className="selectmd"
            value={category3Id.toString()}
            placeholder={category3options[0]?.label || ""}
            isDisabled={isDisabled}
            onChange={(selected) => {
              if (selected && !Array.isArray(selected)) {
                const selectedOption = selected as {
                  value: string;
                  label: string;
                };
                onCategoryChange(id, parseInt(selectedOption.value), 3);
              }
            }}
          />

          <Dropdown
            isClearable={false}
            isSearchable={false}
            options={itemOptions}
            className="selectmd"
            value={itemId.toString()}
            placeholder={itemOptions[0].label}
            isDisabled={isDisabled}
            onChange={(selected) => {
              if (selected && !Array.isArray(selected)) {
                const selectedOption = selected as {
                  value: string;
                  label: string;
                };
                onItemChange(id, selectedOption.value);
              }
            }}
          />

          <div style={{ display: "flex" }}>
            <input
              disabled={isDisabled}
              className="quantity"
              type="number"
              value={quantity}
              onChange={(e) => onQuantityChange(id, e.target.value)}
            />

            <Dropdown
              isClearable={false}
              isSearchable={false}
              options={unitOptions}
              className="selectsm"
              value={unitId.toString()}
              placeholder={category3options && category3options[0].label}
              isDisabled={isDisabled}
              onChange={(selected) => {
                if (selected && !Array.isArray(selected)) {
                  const selectedOption = selected as {
                    value: string;
                    label: string;
                  };
                  onUnitChange(id, selectedOption.value); // Use selectedOption.value directly
                }
              }}
            />
          </div>
          <input
            className="comment"
            disabled={isDisabled}
            type="text"
            value={comment}
            onChange={(e) => onCommentChange(id, e.target.value)}
            placeholder="Kommentar"
          />
        </div>

        {!isInternalUser && (
          <button
            className="trashButton"
            disabled={isDisabled || isDeleteDisabled}
            value={id}
            onClick={() => {
              onRemoveRow(id);
              captureEvent("trash icon");
            }}
          >
            <Icon name="trash" />
          </button>
        )}
      </div>
      {itemComment.length > 0 && (
        <div className="commentRow row">
          <div className="itemComment">
            <p>{itemComment}</p>
          </div>
        </div>
      )}
    </div>
  );
}
